import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { 
  ChartLineUp, Package, FileText, CurrencyCircleDollar, ChartBar,
  ShoppingCart, CalendarBlank, SignOut, LockKey,
  Warehouse, List, Strategy, Gear, Truck, Factory, Trash,
  UsersThree, ForkKnife, PresentationChart, BookOpenText, CheckSquare,
  House, X, User, Brain, MapPin
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import SousChefAI from "@/components/SousChefAI";
import GlobalSearch from "@/components/GlobalSearch";
import ProfileCompletionModal from "@/components/ProfileCompletionModal";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_CONFIG = [
  { section: "section_operation", items: [
    { path: "/", labelKey: "nav_dashboard", icon: ChartLineUp, module: null },
    { path: "/technical-sheets", labelKey: "nav_tech_sheets", icon: FileText, module: null },
    { path: "/ingredients", labelKey: "nav_ingredients", icon: Package, module: null },
    { path: "/checklists", labelKey: "nav_checklists", icon: CheckSquare, module: "checklists" },
  ]},
  { section: "section_planning", items: [
    { path: "/events", labelKey: "nav_events", icon: CalendarBlank, module: null },
    { path: "/attack-plan", labelKey: "nav_attack_plan", icon: Strategy, module: null },
    { path: "/shopping-list", labelKey: "nav_shopping", icon: ShoppingCart, module: null },
    { path: "/menu-builder", labelKey: "nav_menu_builder", icon: BookOpenText, module: "menu_builder" },
  ]},
  { section: "section_financial", items: [
    { path: "/pricing", labelKey: "nav_pricing", icon: CurrencyCircleDollar, module: null },
    { path: "/dre", labelKey: "nav_dre", icon: ChartLineUp, module: "financial_intelligence" },
    { path: "/analytics", labelKey: "nav_analytics", icon: ChartBar, module: null },
    { path: "/menu-engineering", labelKey: "nav_menu_eng", icon: ForkKnife, module: "menu_intelligence" },
  ]},
  { section: "section_stock", items: [
    { path: "/stock", labelKey: "nav_stock", icon: Warehouse, module: null },
    { path: "/suppliers", labelKey: "nav_suppliers", icon: Truck, module: "advanced_stock" },
    { path: "/production-orders", labelKey: "nav_production_orders", icon: Factory, module: "advanced_production" },
    { path: "/waste-control", labelKey: "nav_waste", icon: Trash, module: "advanced_production" },
  ]},
  { section: "section_admin", items: [
    { path: "/team", labelKey: "nav_team", icon: UsersThree, module: "rbac" },
    { path: "/pracas", labelKey: "nav_pracas", icon: MapPin, module: "rbac" },
    { path: "/bi-dashboard", labelKey: "nav_reports", icon: PresentationChart, module: "business_intelligence" },
    { path: "/chef-memory", labelKey: "nav_memory", icon: Brain, module: null },
  ]},
];

// Bottom nav items for mobile (4 most important daily actions)
const BOTTOM_NAV = [
  { path: "/", icon: House, labelKey: "nav_dashboard" },
  { path: "/technical-sheets", icon: FileText, labelKey: "nav_tech_sheets" },
  { path: "/checklists", icon: CheckSquare, labelKey: "nav_checklists" },
  { path: "/shopping-list", icon: ShoppingCart, labelKey: "nav_shopping" },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuth();
  const { t } = useLanguage();
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const dismissed = user?.id && localStorage.getItem(`profile_prompt_dismissed_${user.id}`);
    if (user && !user.country && !dismissed) setShowProfileCompletion(true);
  }, [user]);

  useEffect(() => { setSidebarOpen(false); setMobileMenuOpen(false); }, [location.pathname]);
  
  const handleLogout = async () => { await logout(); navigate('/login'); };
  const resetPasswordForm = () => { setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); setPasswordError(""); };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    if (newPassword.length < 6) { setPasswordError(t('pwd_too_short')); return; }
    if (newPassword !== confirmPassword) { setPasswordError(t('pwd_mismatch')); return; }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      toast.success(t('pwd_changed'));
      setShowPasswordDialog(false);
      resetPasswordForm();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPasswordError(typeof detail === "string" ? detail : "Erro ao alterar senha");
    } finally { setChangingPassword(false); }
  };

  const navSections = useMemo(() => {
    const enabledModules = user?.enabled_modules || [];
    return NAV_CONFIG
      .map(section => ({
        section: t(section.section),
        items: section.items
          .filter(item => !item.module || enabledModules.includes(item.module))
          .map(item => ({ ...item, label: t(item.labelKey) })),
      }))
      .filter(section => section.items.length > 0);
  }, [t, user]);

  const allNavItems = useMemo(() => {
    const items = [];
    navSections.forEach((section) => { items.push(...section.items); });
    if (!user?.is_employee) {
      items.push({ path: "/module-center", label: t('nav_module_center'), icon: Gear });
    }
    return items;
  }, [navSections, user, t]);
  
  const ATLAS_CORE_URL = "https://fawkesatlascore.link";

  const handleAtlasSSO = (closeMobile = false) => {
    if (closeMobile) setMobileMenuOpen(false);
    window.open(ATLAS_CORE_URL, "_blank", "noopener");
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {sidebarOpen && <div className="fixed inset-0 bg-zinc-900/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Desktop/Tablet Sidebar - hidden on mobile */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-60 bg-white border-r border-zinc-200 flex-col z-50 transition-transform duration-300 hidden md:flex ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} md:translate-x-0`}>
        <div className="px-5 py-5 border-b border-zinc-100">
          <Link to="/" className="block">
            <h1 className="text-lg font-extrabold tracking-tight text-zinc-950">SOP & RAFT</h1>
            <p className="text-[9px] tracking-[0.05em] uppercase text-zinc-400 mt-0.5 font-semibold leading-tight">{t('app_full')}</p>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {navSections.map((section) => (
            <div key={section.section} className="mb-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 px-3 mb-1">{section.section}</p>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link key={item.path} to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replaceAll(' ', '-')}`}
                      className={`sidebar-link ${isActive ? 'active' : 'text-zinc-500 hover:text-zinc-900'}`}>
                      <Icon size={18} weight={isActive ? "bold" : "regular"} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
          {!user?.is_employee && (
          <div className="mt-2 pt-2 border-t border-zinc-100">
            <Link to="/module-center" data-testid="nav-central-de-modulos"
              className={`sidebar-link ${location.pathname === '/module-center' ? 'active' : 'text-zinc-500 hover:text-zinc-900'}`}>
              <Gear size={18} weight={location.pathname === '/module-center' ? "bold" : "regular"} />
              <span>{t('nav_module_center')}</span>
            </Link>
          </div>
          )}
          <div className="mt-2 pt-2 border-t border-zinc-100">
            <button onClick={() => handleAtlasSSO(false)} data-testid="atlas-sso-link"
              className="sidebar-link w-full text-zinc-500 hover:text-zinc-900">
              <img src="/atlas-core-icon.png" alt="Fawkes Atlas Core" className="w-[18px] h-[18px] rounded-full shrink-0" />
              <span>Fawkes Atlas Core</span>
              <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-amber-600">SSO</span>
            </button>
          </div>
        </nav>

        <div className="border-t border-zinc-100 p-3">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-8 h-8 rounded-full bg-zinc-900 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-900 truncate leading-tight">{user?.name}</p>
              <p className="text-[10px] text-zinc-400 truncate">
                {user?.is_employee && <span className="text-blue-600 mr-1">[{t('tm_employee_badge') || 'Membro'}]</span>}
                {user?.email}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-0.5">
            <button onClick={() => navigate("/profile")}
              className="flex flex-col items-center gap-0.5 py-1.5 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 transition-colors" data-testid="profile-btn">
              <User size={15} />
              <span className="text-[9px] font-medium">Perfil</span>
            </button>
            <button onClick={() => setShowPasswordDialog(true)}
              className="flex flex-col items-center gap-0.5 py-1.5 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 transition-colors" data-testid="change-password-btn">
              <LockKey size={15} />
              <span className="text-[9px] font-medium">Senha</span>
            </button>
            <button onClick={handleLogout}
              className="flex flex-col items-center gap-0.5 py-1.5 rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid="logout-btn">
              <SignOut size={15} />
              <span className="text-[9px] font-medium">Sair</span>
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen pb-16 md:pb-0">
        {/* Header - search bar */}
        <header className="flex items-center gap-3 px-3 md:px-8 py-2.5 bg-white border-b border-zinc-200 sticky top-0 z-30">
          <div className="flex-1">
            <GlobalSearch />
          </div>
          {/* Mobile user avatar */}
          <button onClick={() => setMobileMenuOpen(true)} className="md:hidden w-8 h-8 rounded-full bg-zinc-900 flex items-center justify-center text-white text-xs font-bold flex-shrink-0" data-testid="mobile-user-btn">
            {user?.name?.charAt(0)?.toUpperCase() || '?'}
          </button>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>

      {/* ========== MOBILE BOTTOM NAV ========== */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-zinc-200 md:hidden safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around px-2 py-1">
          {BOTTOM_NAV.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}
                className={`flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-lg transition-colors min-w-[60px] ${
                  isActive ? 'text-zinc-900' : 'text-zinc-400'
                }`} data-testid={`bnav-${item.path.replace('/', '') || 'home'}`}>
                <Icon size={22} weight={isActive ? "fill" : "regular"} />
                <span className="text-[9px] font-semibold">{t(item.labelKey)}</span>
              </Link>
            );
          })}
          {/* More button */}
          <button onClick={() => setMobileMenuOpen(true)}
            className="flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-lg text-zinc-400 min-w-[60px]" data-testid="bnav-more">
            <List size={22} />
            <span className="text-[9px] font-semibold">Mais</span>
          </button>
        </div>
      </nav>

      {/* ========== MOBILE FULL MENU ========== */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[280px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between p-4 border-b border-zinc-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-zinc-900 flex items-center justify-center text-white text-sm font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="text-sm font-bold text-zinc-900">{user?.name}</p>
                  <p className="text-[10px] text-zinc-400 truncate max-w-[160px]">{user?.email}</p>
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-400 p-1">
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto py-2">
              {navSections.map((section) => (
                <div key={section.section} className="mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 px-4 mb-1">{section.section}</p>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm ${isActive ? 'text-zinc-900 font-semibold bg-zinc-50' : 'text-zinc-600'}`}>
                        <Icon size={18} weight={isActive ? "bold" : "regular"} />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              ))}
              {!user?.is_employee && (
                <Link to="/module-center" onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-600 border-t border-zinc-100 mt-2 pt-2">
                  <Gear size={18} /> <span>{t('nav_module_center')}</span>
                </Link>
              )}
              <button onClick={() => handleAtlasSSO(true)} data-testid="mobile-atlas-sso-link"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-600 border-t border-zinc-100 mt-2 pt-2 w-full">
                <img src="/atlas-core-icon.png" alt="Fawkes Atlas Core" className="w-[18px] h-[18px] rounded-full shrink-0" />
                <span>Fawkes Atlas Core</span>
                <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-amber-600">SSO</span>
              </button>
            </div>

            <div className="border-t border-zinc-100 p-3">
              <div className="grid grid-cols-4 gap-0.5">
                <button onClick={() => { setMobileMenuOpen(false); navigate("/profile"); }}
                  className="flex flex-col items-center gap-0.5 py-2 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 transition-colors" data-testid="mobile-profile-btn">
                  <User size={16} />
                  <span className="text-[9px] font-medium">Perfil</span>
                </button>
                <button onClick={() => { setMobileMenuOpen(false); setShowPasswordDialog(true); }}
                  className="flex flex-col items-center gap-0.5 py-2 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 transition-colors">
                  <LockKey size={16} />
                  <span className="text-[9px] font-medium">Senha</span>
                </button>
                <button onClick={handleLogout}
                  className="flex flex-col items-center gap-0.5 py-2 rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                  <SignOut size={16} />
                  <span className="text-[9px] font-medium">Sair</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <SousChefAI />

      <Dialog open={showPasswordDialog} onOpenChange={(open) => { setShowPasswordDialog(open); if (!open) resetPasswordForm(); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <LockKey size={20} className="text-zinc-700" /> {t('pwd_change')}
            </DialogTitle>
            <DialogDescription className="sr-only">{t('pwd_change')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-2">
            {passwordError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="password-error">
                <p className="text-sm text-red-700 font-medium">{passwordError}</p>
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('pwd_current')}</Label>
              <Input type="password" value={currentPassword} onChange={(e) => { setCurrentPassword(e.target.value); setPasswordError(""); }}
                required placeholder={t('pwd_current_placeholder')} className="mt-1.5" data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('pwd_new')}</Label>
              <Input type="password" value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setPasswordError(""); }}
                required minLength={6} placeholder={t('pwd_new_placeholder')} className="mt-1.5" data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('pwd_confirm')}</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(""); }}
                required minLength={6} placeholder={t('pwd_confirm_placeholder')} className="mt-1.5" data-testid="confirm-password-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowPasswordDialog(false); resetPasswordForm(); }}>{t('cancel')}</Button>
              <Button type="submit" disabled={changingPassword} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="submit-change-password-btn">
                {changingPassword ? "..." : t('pwd_change')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {showProfileCompletion && (
        <ProfileCompletionModal user={user} onComplete={() => {
          if (user?.id) localStorage.setItem(`profile_prompt_dismissed_${user.id}`, "1");
          setShowProfileCompletion(false);
        }} />
      )}
    </div>
  );
}
